import type { BadgeType } from '@skytech/ui/Badge'
import { MS_PER_DAY } from '@skytech/utils'

type LeaseBadgeColor = 'grey' | 'green' | 'yellow' | 'red' | 'darkRed'
type LeaseBadgeBucket = { value: number; unit: 'months' | 'years' }

export type LeaseBadgeInfo = {
  color: LeaseBadgeColor
  days: number // always >= 0 - magnitude only
  isOverdue: boolean
  bucket: LeaseBadgeBucket | null // set when daysLeft > 90 (grey)
}

const toStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const getDaysDiff = (from: Date, to: Date) =>
  Math.round((toStartOfDay(to).getTime() - toStartOfDay(from).getTime()) / MS_PER_DAY)

const getLeaseBadgeColor = (daysLeft: number): LeaseBadgeColor => {
  if (daysLeft > 90) return 'grey' // more than 90 days left
  if (daysLeft >= 61) return 'green' // 61..90 days left
  if (daysLeft >= 31) return 'yellow' // 31..60 days left
  if (daysLeft >= 10) return 'red' // 10..30 days left
  return 'darkRed' // 9..0 days left, and any overdue (negative)
}

const getLeaseBadgeBucket = (daysLeft: number): LeaseBadgeBucket | null => {
  if (daysLeft <= 90) return null
  if (daysLeft < 180) return { value: 3, unit: 'months' }
  if (daysLeft < 270) return { value: 6, unit: 'months' }
  if (daysLeft < 365) return { value: 9, unit: 'months' }
  return { value: Math.floor(daysLeft / 365), unit: 'years' }
}

export const getLeaseBadgeInfo = (
  leaseEnd: string | null,
  today: Date = new Date()
): LeaseBadgeInfo | null => {
  if (!leaseEnd) return null

  const leaseEndDate = new Date(leaseEnd)
  if (Number.isNaN(leaseEndDate.getTime())) return null

  const daysLeft = getDaysDiff(today, leaseEndDate)
  return {
    color: getLeaseBadgeColor(daysLeft),
    days: Math.abs(daysLeft),
    isOverdue: daysLeft < 0,
    bucket: getLeaseBadgeBucket(daysLeft),
  }
}

export const badgeTypeByColor: Record<LeaseBadgeColor, BadgeType> = {
  grey: 'info',
  green: 'positive',
  yellow: 'neutral',
  red: 'negative',
  darkRed: 'critical',
}
